import React from "react";
import { Box, useMediaQuery } from "@mui/material";
import {
  PrecisionManufacturingOutlined,
} from "@mui/icons-material";
import { dt } from "../../../shared/themes/redesignTokens";
import { CatalogModelListItem } from "../models/models";
import { formatManufacturerWithHierarchy } from "../../../shared/utils/machineUtils";
import useTranslation from "../../../shared/hooks/useTranslation";
import PermissionGuard from "../../../shared/components/PermissionGuard";
import { Modules, PermissionLevel } from "../../../core/models/PermissionModels";

interface CatalogModelCardProps {
  model: CatalogModelListItem;
  index: number;
  onClick: (model: CatalogModelListItem) => void;
  onAddMachine?: (model: CatalogModelListItem) => void;
}

const categoryColorMap: Record<string, string> = {
  "machining-centers": dt.blue,
  "turning-machines": "#8E44AD",
  "grinding-machines": "#E67E22",
  "edm": "#27AE60",
  "laser-machines": "#E74C3C",
};

const categoryLabelMap: Record<string, string> = {
  "machining-centers": "MILLING",
  "turning-machines": "TURNING",
  "grinding-machines": "GRINDING",
  "edm": "EDM",
  "laser-machines": "LASER",
};

const platformCountDt = {
  bg: "rgba(39, 174, 96, 0.08)",
  text: "#27AE60",
  border: "rgba(39, 174, 96, 0.15)",
};

const CatalogModelCard: React.FC<CatalogModelCardProps> = ({
  model,
  index,
  onClick,
  onAddMachine,
}) => {
  const isPhone = useMediaQuery("(max-width:768px)");
  const { translate } = useTranslation("myMachinery");

  const categoryColor = categoryColorMap[model.categoryCode] || dt.blue;
  const categoryLabel = categoryLabelMap[model.categoryCode] || model.categoryCode;
  const unitsOnPlatform = (model as any).unitsOnPlatform ?? 0;

  return (
    <Box
      onClick={() => onClick(model)}
      sx={{
        backgroundColor: dt.cardBg,
        borderRadius: isPhone ? dt.radiusSm : dt.radius,
        border: `1px solid ${dt.cardBorder}`,
        boxShadow: dt.cardShadow,
        padding: isPhone ? "12px" : "16px",
        cursor: "pointer",
        transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        animation: `cardReveal 0.4s cubic-bezier(0.4, 0, 0.2, 1) ${Math.min(index * 0.06, 0.6)}s both`,
        "&:hover": {
          boxShadow: dt.cardShadowHover,
          transform: "translateY(-4px)",
          borderColor: `${categoryColor}40`,
        },
      }}
    >
      {/* Top row: Category badge + Platform count */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
        }}
      >
        {/* Category badge */}
        <Box
          sx={{
            fontSize: "10px",
            fontWeight: 600,
            fontFamily: dt.font,
            textTransform: "uppercase",
            letterSpacing: "0.8px",
            color: categoryColor,
            backgroundColor: `${categoryColor}10`,
            borderRadius: "6px",
            padding: "3px 8px",
          }}
        >
          {categoryLabel}
        </Box>

        {/* Platform count badge (only when > 0) */}
        {unitsOnPlatform > 0 && (
          <Box
            sx={{
              fontSize: "11px",
              fontFamily: dt.fontMono,
              fontWeight: 600,
              color: platformCountDt.text,
              backgroundColor: platformCountDt.bg,
              border: `1px solid ${platformCountDt.border}`,
              borderRadius: "10px",
              padding: "2px 8px",
            }}
          >
            {unitsOnPlatform} units
          </Box>
        )}
      </Box>

      {/* Main content: Image placeholder + text */}
      <Box sx={{ display: "flex", gap: 1.5 }}>
        {/* Image placeholder */}
        <Box
          sx={{
            width: isPhone ? 60 : 80,
            height: isPhone ? 60 : 64,
            borderRadius: dt.radiusSm,
            backgroundColor: `${categoryColor}08`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            overflow: "hidden",
          }}
        >
          {(model.imageStandardUrl || model.imageUrl) ? (
            <img
              src={model.imageStandardUrl || model.imageUrl || ""}
              alt={model.modelName}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          ) : (
            <PrecisionManufacturingOutlined
              sx={{ fontSize: isPhone ? 24 : 28, color: `${categoryColor}60` }}
            />
          )}
        </Box>

        {/* Text info */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Model name */}
          <Box
            sx={{
              fontSize: isPhone ? "14px" : "15px",
              fontWeight: 600,
              fontFamily: dt.font,
              color: dt.textPrimary,
              letterSpacing: "-0.1px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {model.modelName}
          </Box>

          {/* Manufacturer name */}
          <Box
            sx={{
              fontSize: "12px",
              fontWeight: 400,
              fontFamily: dt.font,
              color: dt.textSecondary,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              mt: 0.25,
            }}
          >
            {model.manufacturer
              ? formatManufacturerWithHierarchy(
                  model.manufacturer.name,
                  model.manufacturer.parentManufacturerName
                )
              : ""}
          </Box>

          {/* SubType */}
          {model.subType && (
            <Box
              sx={{
                fontSize: "11px",
                fontWeight: 400,
                fontFamily: dt.font,
                color: dt.textMuted,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                mt: 0.25,
              }}
            >
              {model.subType}
            </Box>
          )}
        </Box>
      </Box>

      {/* Key specs row (Phase 7F) */}
      {(model.maxSpindleRpm || model.spindleHp || model.toolCapacity) && (
        <Box
          sx={{
            display: "flex",
            gap: isPhone ? "8px" : "12px",
            flexWrap: "wrap",
            mt: 1,
          }}
        >
          {model.maxSpindleRpm && (
            <Box sx={{ fontSize: "11px", fontFamily: 'var(--font-mono)', fontWeight: 500, color: dt.textSecondary }}>
              {model.maxSpindleRpm.toLocaleString()} <Box component="span" sx={{ color: dt.textMuted, fontSize: "10px" }}>RPM</Box>
            </Box>
          )}
          {model.spindleHp && (
            <Box sx={{ fontSize: "11px", fontFamily: 'var(--font-mono)', fontWeight: 500, color: dt.textSecondary }}>
              {model.spindleHp} <Box component="span" sx={{ color: dt.textMuted, fontSize: "10px" }}>HP</Box>
            </Box>
          )}
          {model.toolCapacity && (
            <Box sx={{ fontSize: "11px", fontFamily: 'var(--font-mono)', fontWeight: 500, color: dt.textSecondary }}>
              {model.toolCapacity} <Box component="span" sx={{ color: dt.textMuted, fontSize: "10px" }}>tools</Box>
            </Box>
          )}
          {model.taperTypeName && (
            <Box sx={{ fontSize: "10px", fontWeight: 600, fontFamily: 'var(--font-mono)', color: "#8E44AD", backgroundColor: "rgba(142, 68, 173, 0.08)", borderRadius: "8px", padding: "1px 6px" }}>
              {model.taperTypeName}
            </Box>
          )}
        </Box>
      )}

      {/* Divider */}
      {!isPhone && (
        <Box sx={{ borderTop: `1px solid ${dt.rowBorder}`, margin: "12px 0 8px 0" }} />
      )}

      {/* Footer (desktop/tablet only) */}
      {!isPhone && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {/* Left: Units on platform / New to catalog */}
          <Box
            sx={{
              fontSize: "11px",
              fontFamily: dt.fontMono,
              fontWeight: 600,
              color: unitsOnPlatform > 0 ? platformCountDt.text : dt.textMuted,
            }}
          >
            {unitsOnPlatform > 0
              ? translate("unitsOnPlatform", { count: unitsOnPlatform })
              : translate("newToCatalog")}
          </Box>

          {/* Right: Add button + View Specs link */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {onAddMachine && (
              <PermissionGuard module={Modules.Machinery} level={PermissionLevel.Write}>
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    onAddMachine(model);
                  }}
                  sx={{
                    fontSize: "11px",
                    fontWeight: 600,
                    fontFamily: dt.font,
                    color: dt.blue,
                    backgroundColor: `${dt.blue}10`,
                    borderRadius: "10px",
                    padding: "3px 10px",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                    "&:hover": {
                      backgroundColor: `${dt.blue}20`,
                    },
                  }}
                >
                  {translate("addToMyMachines")}
                </Box>
              </PermissionGuard>
            )}

          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CatalogModelCard;
